import React, { useEffect, useState } from 'react';
import { createTheme, ThemeProvider, styled } from '@mui/material/styles';
import {
  CssBaseline,
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  LinearProgress,
  InputBase,
  Paper,
  Chip
} from '@mui/material';

// Material Icons
import SearchIcon from '@mui/icons-material/Search';
import NotificationsIcon from '@mui/icons-material/Notifications';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import MovieFilterIcon from '@mui/icons-material/MovieFilter'; // App Logo Icon
import MovieIcon from '@mui/icons-material/Movie';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { CircularProgress } from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const URL = 'https://port-0-aiscenesever-iad5e2alq3bhn8p.sel4.cloudtype.app';


// ----------------------------------------------------
// 1. THEME SETUP (순수 블랙 배경 및 강조색 설정)
// ----------------------------------------------------

const primaryColor = '#EF4444'; // Tailwind red-500 equivalent

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: primaryColor,
    },
    background: {
      // 배경색을 순수 검은색으로 변경 (#000000)
      default: '#000000',
      // 카드 배경색은 순수 검은색과 대비되도록 아주 어두운 회색으로 설정
      paper: '#0A0A0A',
    },
    text: {
      primary: '#F9FAFB',
      secondary: '#9CA3AF',
    },
  },
  typography: {
    fontFamily: 'Roboto, sans-serif',
    h1: {
      fontWeight: 800,
    },
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          // AppBar 배경도 검은색을 기반으로 투명도 및 블러 효과 적용
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(4px)',
          borderBottom: '1px solid #1A1A1A', // Subtle border
        },
      },
    },
  },
});

// ----------------------------------------------------
// 2. STYLED COMPONENTS (커스텀 스타일링)
// ----------------------------------------------------

// 커스텀 검색창 스타일
const Search = styled(Paper)(({ theme }) => ({
  position: 'relative',
  borderRadius: 9999, // full rounded
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[6],
  '&:hover': {
    backgroundColor: '#1A1A1A', // Darker hover
  },
  '&.Mui-focused': {
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
  },
  maxWidth: 512,
  margin: '0 auto',
  border: 'none',
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  left: 0,
  color: theme.palette.text.secondary,
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1.5, 1.5, 1.5, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
  },
}));

// ----------------------------------------------------
// 3. COMPONENTS
// ----------------------------------------------------




const MovieCard = ({ navigate, uniqueCode, profileImage, user_id, title, creator, viewCount, likeCount, thumbUrl, creatorAvatar, postType }) => (

  <Card
    onClick={() => navigate(`/post/${postType}/${uniqueCode}`)}
    elevation={4}
    sx={{
      width: {
        xs: '100%',   // 📱 모바일에서는 화면 가득
        sm: 260,      // 📄 태블릿부터 고정폭
      },
      maxWidth: 260,
      borderRadius: 3,
      bgcolor: 'background.paper',
      cursor: 'pointer',
      mx: 'auto',
      transition: 'box-shadow 0.3s',
      '&:hover': {
        boxShadow: `0 10px 15px -3px rgba(239, 68, 68, 0.3), 
                  0 4px 6px -2px rgba(239, 68, 68, 0.1)`
      },
    }}
  >
    <Box sx={{ position: 'relative', overflow: 'hidden', mb: 1.5 }}>
      <CardMedia
        component="img"
        image={thumbUrl}
        alt={`${title} poster`}
        sx={{
          width: '100%',
          height: 320,          // 🔹 원하는 고정 높이 (px 또는 rem, vh 등 사용 가능)
          objectFit: 'cover',   // 🔹 비율 유지하며 꽉 채우기
          borderRadius: '12px',
          transition: 'transform 0.3s',
          '&:hover': { transform: 'scale(1.02)' },
        }}
      />
      <Box sx={{
        position: 'relative',
        zIndex: 1,
        // paddingTop: POSTER_ASPECT_RATIO_PADDING, // Filler for aspect ratio
        height: 0
      }} />

      {/* Movie Chip (ShortCard와 구분하기 위해 유지) */}
      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
        <Chip
          icon={<MovieIcon sx={{ fontSize: 16, mr: 0.5 }} />}
          label={postType == 'movie' ? "Movie" : "Series"}
          size="small"
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            fontSize: 12,
            fontWeight: 'medium',
            opacity: 0.9,
          }}
        />
      </Box>
    </Box>

    <CardContent sx={{ p: 1.5, pt: 0, display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Avatar alt="Creator avatar" src={profileImage} sx={{ width: 32, height: 32, border: '1px solid #4B5563' }} />
      <Box sx={{ flexGrow: 1 }}>
        <Typography
          variant="subtitle1"
          fontWeight="semibold"
          sx={{
            lineHeight: 1.2,
            mb: 0.5,
            color: 'text.primary',
            whiteSpace: 'nowrap',        // 1줄 고정
            overflow: 'hidden',
            textOverflow: 'ellipsis',    // ... 표시
            '&:hover': { color: 'primary.main' },
            transition: 'color 0.15s'
          }}
        >
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">{user_id}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5, fontSize: 14, color: 'text.secondary' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FavoriteBorderIcon sx={{ fontSize: 16, mr: 0.5 }} />{likeCount}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <VisibilityIcon sx={{ fontSize: 16, mr: 0.5 }} />{viewCount}
          </Box>
        </Box>
      </Box>
    </CardContent>
  </Card>
);


const Home = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(URL + '/GetPost/Web')
      .then((res) => {
        setData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
      })
  }, [])
  const popularMovies = [
    {
      id: 1,
      //postId:"",
      title: "Eclipse of Tomorrow",
      description:
        "In a world where sunlight has been blocked by a massive artificial shield, a former solar engineer uncovers a conspiracy that threatens the last remnants of humanity. With time running out, she must risk everything to bring back the light before society collapses into irreversible darkness.",
      genre: "Sci-Fi / Thriller",
      year: 2026,
      creator: "Aiden Lockhart",
      likes: "420K",
      views: "1.1M",
      imageUrl:
        "https://aiscene.s3.ap-southeast-2.amazonaws.com/post/image/1764388711332_b886acbd-fc91-4663-9468-908b64bb3fc1.jpg"
    },
    {
      id: 2,
      title: "The Last Melody",
      description:
        "When a world-renowned violinist loses his hearing overnight, he struggles to find purpose in a silent world. His life changes when he meets a mysterious street musician whose haunting melody may hold the key to restoring not just his hearing, but his soul.",
      genre: "Drama / Music",
      year: 2023,
      creator: "Elena Verdana",
      likes: "310K",
      views: "870K",
      imageUrl:
        "https://aiscene.s3.ap-southeast-2.amazonaws.com/post/image/1764389033989_7dcb1284-fc57-4fe1-9a33-ab92da5786fc.jpg"
    },
    {
      id: 3,
      title: "Whispers in the Fog",
      description:
        "After moving to a quiet seaside town for a fresh start, a detective becomes entangled in a series of strange disappearances. As he dig deeper, he discovers that the fog hiding the town each night carries an ancient secret—one that knows exactly who he is.",
      genre: "Mystery / Thriller",
      year: 2025,
      creator: "Harper Quinn",
      likes: "280K",
      views: "690K",
      imageUrl:
        "https://aiscene.s3.ap-southeast-2.amazonaws.com/post/image/1764388890033_bdbb53ff-8beb-4ba1-82ac-520eaf4e7da7.jpg"
    },

    // 🎬 추가된 3개의 새로운 영화
    {
      id: 4,
      title: "Garden of Echoes",
      description:
        "In a secluded botanical garden where every plant holds a recorded memory, a grieving journalist discovers a flower that contains the final moments of her lost brother — forcing her to decide whether some truths are better left buried.",
      genre: "Fantasy / Mystery",
      year: 2024,
      creator: "Lia Montgomery",
      likes: "190K",
      views: "540K",
      imageUrl:
        "https://aiscene.s3.ap-southeast-2.amazonaws.com/post/image/1764389978183_daab6c9a-668d-4f3f-b119-413060d3e1d8.jpg"
    },
    {
      id: 5,
      title: "Steel Hearts",
      description:
        "In a futuristic arena where AI-driven mechs battle for global entertainment, a retired pilot is forced back into the game to control an obsolete machine—only to prove that human instinct can still rival advanced technology.",
      genre: "Action / Sci-Fi",
      year: 2027,
      creator: "Marcus Rivera",
      likes: "230K",
      views: "610K",
      imageUrl:
        "https://aiscene.s3.ap-southeast-2.amazonaws.com/post/image/1764390470630_28627862-b971-4a1b-aea0-656c1e54f628.jpg"
    },

  ];

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // bgcolor: 'background.default' 
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }
  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
     
      {/* 순수 검은색 배경 적용 */}
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <AppBar position="sticky" elevation={0} sx={{ borderBottom: '1px solid #1A1A1A' }}>
          <Container maxWidth="lg">
            <Toolbar disableGutters sx={{ minHeight: 64, justifyContent: 'space-between' }}>
              {/* Logo and App Name */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  component="img"
                  src="/logo.png"   // public 폴더 안의 파일 경로
                  alt="movie icon"
                  sx={{
                    width: 32,
                    height: 32,
                    objectFit: "contain",
                  }}
                />
                <Typography
                  variant="h6"
                  component="div"
                  sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                  Alscene
                </Typography>
              </Box>

              {/* Action Buttons and Avatar */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0, sm: 1 } }}>
                <IconButton sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'background.paper' } }}>
                  <NotificationsIcon />
                </IconButton>
                <IconButton sx={{ color: 'text.secondary', '&:hover': { bgcolor: 'background.paper' } }}>
                  <SearchIcon />
                </IconButton>
                <Avatar
                  //alt="User avatar"
                  src="https://lh3.googleusercontent.com/aida-public/AB6AXuCBhc1RyMhNzlOaQsf1u38zjdhyGuG9slN8g43pvf_r3ukNNh6qVDJ8_uB2tVlG9UzTHCHJyr6ZR5ou7U_j4lFXiRkphJIFvT19iRz5Lc1JSTj3Z1wt_wXB9qcho32ZR6nphO_MfaLWN0zGQ0B2vTI17khi9rXJGsyeMxCyU1NgAJpxdFY7p_QZ3CXZwpjzcPlWqsGK4-pq_X_kzph19uKpq-6PNeFuL5O2J4zf5eRBvQdRiYLfgOaTM6IUL08faDjdQr8ryH9b0zve"
                  sx={{ width: 40, height: 40, border: '2px solid rgba(255, 0, 0, 1)' }} // Red border
                />
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
        <Container component="main" maxWidth="lg" sx={{ py: 5 }}>

          {/* Hero Section (Search) */}
          <Box sx={{ mb: 0, textAlign: 'center' }}>
            <Typography variant="h1" sx={{
              fontSize: { xs: '2.25rem', md: '3rem' },
              mb: 2,
              color: 'white'
            }}>
              WELCOME TO <Typography variant="h1" sx={{
                fontSize: { xs: '2.25rem', md: '3rem' },
                mb: 2,
                color: 'red'
              }} color="primary" inherit>AIscene</Typography>
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{
              maxWidth: 600,
              mx: 'auto',
              mb: 4,
              fontSize: { xs: '1rem', sm: '1.125rem' }
            }}>
              watch AI movies and drama series
            </Typography>

        <Button
  onClick={() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;

    if (/android/i.test(userAgent)) {
      console.log("Android");
        window.location.href = "https://play.google.com/store/apps/details?id=com.juseung.aiscenes";
    } else if (/iPad|iPhone|iPod/.test(userAgent)) {
      console.log("iOS");
      window.location.href = "https://apps.apple.com/app/id6754524422";
    } else {
      console.log("Desktop");

    }

  }}
  sx={{
    width: '100%',
    height: 50,
    borderRadius: 3,
    textTransform: 'none',
    justifyContent: 'flex-start',
    pl: 2,
    bgcolor: '#2a2a2c',
    color: 'white',
  }}
>
  <SearchIcon sx={{ mr: 1, opacity: 0.6 }} />
  <Typography sx={{ opacity: 0.6 }}>
    Search for movies, shorts, or creators...
  </Typography>
</Button>
          </Box>
             <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 4,mt:4 }}>

  {/* ANDROID BUTTON */}
  <Button
    variant="contained"
    onClick={() => {
      // Android (삼성)
      window.location.href = "https://play.google.com/store/apps/details?id=com.juseung.aiscenes";
    }}
    sx={{
      px: 4,
      py: 1.5,
      fontWeight: 'bold',
      fontSize: '1rem',
      borderRadius: '12px',
      textTransform: 'none',
      background: 'linear-gradient(90deg, #4C9CFF, #005CFF)', // blue gradient
      boxShadow: '0 4px 12px rgba(0, 120, 255, 0.35)',
      '&:hover': {
        background: 'linear-gradient(90deg, #3A8BFF, #004EE0)',
        boxShadow: '0 4px 14px rgba(0, 120, 255, 0.45)',
      }
    }}
  >
    Open App (Android)
  </Button>

  {/* IOS BUTTON */}
  <Button
    variant="contained"
    onClick={() => {
      // iPhone
      window.location.href = "https://apps.apple.com/app/id6754524422";
    }}
    sx={{
      px: 4,
      py: 1.5,
      fontWeight: 'bold',
      fontSize: '1rem',
      borderRadius: '12px',
      textTransform: 'none',
      background: 'linear-gradient(90deg, #FF3E3E, #FF6A00)', // red-orange gradient
      boxShadow: '0 4px 12px rgba(255, 80, 40, 0.35)',
      '&:hover': {
        background: 'linear-gradient(90deg, #FF2A2A, #FF5500)',
        boxShadow: '0 4px 14px rgba(255, 80, 40, 0.45)',
      }
    }}
  >
    Open App (iPhone)
  </Button>

</Box>
          {/* Popular Movies Section */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.75rem', md: '2rem' },
                fontWeight: 'bold',
                mb: 3,
                color: 'white',
                borderLeft: '4px solid',
                borderColor: 'primary.main',
                pl: 2
              }}
            >
              🔥 Popular Episodes
            </Typography>
            <div
              style={{
                display: 'flex',
                overflowX: 'auto',
                gap: '16px',
                paddingBottom: '10px',

                /* 스크롤바 숨기기 */
                scrollbarWidth: 'none',        // Firefox
                msOverflowStyle: 'none',       // IE, Edge
              }}
              className="horizontal-scroll"
            >
              {data.map((movie) => (
                <div
                  key={movie.id}
                  style={{
                    flex: '0 0 auto',
                    width: '260px',
                  }}
                >
                  <MovieCard {...movie} navigate={navigate} />
                </div>
              ))}
            </div>
          </Box>
              {
                /**이곳에 앱으로 이동버튼 만들기 */
              }
          {/* Popular Shorts Section */}
              
              
        </Container>
        <Footer />
      </Box>
    </ThemeProvider>
  );
};

// --- Footer Component ---

const Footer = () => (
  <Box component="footer" sx={{
    // footer 배경도 어두운 색상으로 변경
    bgcolor: 'rgba(0,0,0,0.5)',
    borderTop: '1px solid #1A1A1A',
    mt: 8,
    py: 3,
    textAlign: 'center',
    color: 'text.secondary'
  }}>
    <Container maxWidth="lg">
      <Typography variant="body2" sx={{ mb: 1 }}>
        © 2024 Alscenes. All Rights Reserved.
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, fontSize: '0.875rem' }}>
        <Typography component="a" href="#" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}>
          About
        </Typography>
        <Typography component="a" href="#" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}>
          Terms of Service
        </Typography>
        <Typography component="a" href="#" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}>
          Privacy Policy
        </Typography>
        <Typography component="a" href="#" sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}>
          Contact
        </Typography>
      </Box>
    </Container>
  </Box>
);

export default Home;